import React, { createContext, useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import _isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { createErrorBanner } from 'components/Banner';
import { SERVER_DATE_FORMAT, CLASS_ENTITY_TYPE } from 'constants/logic';
import { reportError } from 'helpers/error-logging';
import { useIsScheduleListV2Enabled } from 'hooks/useIsScheduleListV2Enabled';
import {
  selectActiveVenue,
  selectSchedulesStartDate,
  selectUserInfo,
  selectVenueSmartspotData,
  selectVenueSmartspotDataIsLoading,
} from 'selectors/general';

import { fetchScheduleData, messages, setSelectedSchedule } from '../YieldManagement.saga';
import { selectActiveScheduleId } from '../YieldManagement.selectors';
import {
  enrichSchedulesWithOpportunities,
  fetchVenueSchedulesUntilCurrentTime,
  findActiveSchedule,
  findNextUpcomingSchedule,
  getOpportunitiesMap,
  getVenueSchedules,
  mapDerivedSchedulesData,
  resolveScheduleFilterCounts,
} from './VenueSchedules.helpers';

import {
  VENUE_SCHEDULES_CACHE,
  OPPORTUNITIES_CACHE,
  CACHE_EVENT_TARGET,
  CACHE_REFRESH_EVENT,
} from './VenueSchedulesCache';

/**
 * @typedef {Object} ScheduleClass
 * @property {number} classId
 * @property {boolean} classDisabled
 * @property {string} className
 * @property {boolean} classPublished
 * @property {string} classSource
 */

/**
 * @typedef {Object} Resource
 * @property {number} resourceId
 * @property {string} resourceName
 */

/**
 * @typedef {Object} Spots
 * @property {number} booked
 * @property {number} classpassTotal
 * @property {number} classpassBooked
 * @property {number} classpassDemand
 * @property {number} deferredReservations
 * @property {number} unfulfilledDeferredReservations
 * @property {number} maxCapacity
 * @property {number} takenSpots
 * @property {number} total
 */

/**
 * @typedef {Object} Schedule
 * @property {number} apiGuid
 * @property {boolean} apiNoCapacity
 * @property {boolean} classpassAlmostSoldOut
 * @property {boolean} classpassSoldOut
 * @property {number} classpassSpotsUnfilled
 * @property {number} duration
 * @property {Date} endDateTime
 * @property {string} endTime
 * @property {string} entityType
 * @property {boolean} hasIntegrationData
 * @property {boolean} isCancellable
 * @property {boolean} isCancelled
 * @property {boolean} isDisabled
 * @property {boolean} isFull
 * @property {boolean} isInProgress
 * @property {boolean} isInThePast
 * @property {boolean} isLocked
 * @property {boolean} isManualStudio
 * @property {boolean} isWellness
 * @property {Object} opportunity
 * @property {ScheduleClass} scheduleClass
 * @property {boolean} scheduleDisabled
 * @property {number} scheduleId
 * @property {string} scheduleName
 * @property {boolean} schedulePublished
 * @property {boolean} reservationsStopped
 * @property {Resource} resource
 * @property {boolean} smartspotEnabled
 * @property {boolean} smartspotEnabledForVenue
 * @property {string} smartspotMode
 * @property {number} smartspotNetworkSpotCap
 * @property {string} smartspotReason
 * @property {Spots} spots
 * @property {string} source
 * @property {string} startDate
 * @property {Date} startDateTime
 * @property {string} startTime
 * @property {number} venueId
 * @property {string} venueTimezone
 */

/**
 * @typedef {Object} Pagination
 * @property {boolean} hasMore
 * @property {number} page
 * @property {number} pageSize
 * @property {number} totalPages
 */

/**
 * @typedef {Object} FilterCounts
 * @property {number} activeClasses
 * @property {number} disabledClasses
 * @property {number} [withClientsWaiting]
 * @property {number} [withNewClients]
 * @property {number} withReservations
 */

/**
 * @typedef {Object} Value
 * @property {FilterCounts} [filterCounts]
 * @property {boolean} isLoading
 * @property {boolean} isLoadingMore
 * @property {function} loadMoreVenueSchedules
 * @property {Pagination} pagination
 * @property {function} requestNextUpcomingSelection
 * @property {Schedule[]} schedules
 */

const VenueSchedulesContext = createContext();

const getCachedVenueSchedulesForPresentation = (cacheKey, includeListPresentation) => {
  if (!VENUE_SCHEDULES_CACHE.has(cacheKey)) {
    return null;
  }

  const cached = VENUE_SCHEDULES_CACHE.get(cacheKey);
  if (cached?.includeListPresentation !== includeListPresentation) {
    VENUE_SCHEDULES_CACHE.delete(cacheKey);
    return null;
  }

  return cached;
};

export function VenueSchedulesContextProvider({ children }) {
  const dispatch = useDispatch();
  const activeScheduleId = useSelector(selectActiveScheduleId);
  const schedulesStartDate = useSelector(selectSchedulesStartDate);
  const user = useSelector(selectUserInfo);
  const venue = useSelector(selectActiveVenue);
  const venueSmartspotData = useSelector(selectVenueSmartspotData);
  const venueSmartspotDataIsLoading = useSelector(selectVenueSmartspotDataIsLoading);
  // Flag starts null and settles asynchronously; fetching before it settles would request without
  // list_presentation, then again with it (and race). The hook bounds resolution, so waiting is safe.
  const scheduleListV2Flag = useIsScheduleListV2Enabled();
  const includeListPresentation = scheduleListV2Flag === true;

  const [activeSchedule, setActiveSchedule] = useState({});
  const isNextUpcomingSelectionPending = useRef(false);
  const updateFromCacheOrFetchRef = useRef(() => {});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [venueSchedules, setVenueSchedules] = useState({});

  const scheduleStartDateString = schedulesStartDate ? schedulesStartDate.toISOString() : null;
  const { isMsInventoryVenue, timezone: venueTimezone, venueId: activeVenueId } = venue;
  const entityType = isMsInventoryVenue ? CLASS_ENTITY_TYPE.CLASS : '';

  // Same key shape as refreshVenueSchedulesCacheForVenueAndDate; presentation mode is stored on the value.
  const venueIdDateKey = `${activeVenueId}-${format(schedulesStartDate, SERVER_DATE_FORMAT)}`;
  const opportunitiesKey = `${activeVenueId}-opportunities`;

  const fetchMoreVenueSchedules = async ({ date = schedulesStartDate, page, venueId }) => {
    const formattedDate = format(date, SERVER_DATE_FORMAT);
    try {
      return await getVenueSchedules(formattedDate, page, entityType, venueId, includeListPresentation);
    } catch (error) {
      reportError(error);
      dispatch(createErrorBanner(messages.error.fetchingScheduleData));
      return {
        pagination: {},
        schedules: [],
      };
    }
  };

  const fetchVenueSchedules = async (date = schedulesStartDate, page = 0) => {
    const formattedDate = format(date, SERVER_DATE_FORMAT);
    try {
      return await fetchVenueSchedulesUntilCurrentTime(
        formattedDate,
        page,
        entityType,
        activeVenueId,
        venueTimezone,
        includeListPresentation
      );
    } catch (error) {
      reportError(error);
      dispatch(createErrorBanner(messages.error.fetchingScheduleData));
      return {
        pagination: {},
        schedules: [],
      };
    }
  };

  const getDerivedVenueSchedulesData = async (venueSchedulesData, opportunitiesCache, dayKey = venueIdDateKey) => {
    const { filterCounts, pagination, schedules } = venueSchedulesData;
    const schedulesWithOpportunityData = await enrichSchedulesWithOpportunities(opportunitiesCache, schedules);

    return {
      // Consumers reset off these counts, so they carry the day they describe: a caller reading them
      // between a date change and the refetch would otherwise act on the previous day's numbers.
      dayKey,
      filterCounts,
      pagination: {
        ...pagination,
        hasMore: (pagination?.page || 0) < pagination?.totalPages - 1,
      },
      schedules: mapDerivedSchedulesData(schedulesWithOpportunityData, venue, venueSmartspotData),
    };
  };

  const requestNextUpcomingSelection = () => {
    isNextUpcomingSelectionPending.current = true;
  };

  const selectNextUpcomingWhenRequested = schedules => {
    if (!isNextUpcomingSelectionPending.current) {
      return;
    }
    isNextUpcomingSelectionPending.current = false;

    const nextSchedule = findNextUpcomingSchedule(schedules, venueTimezone);
    if (nextSchedule) {
      dispatch(setSelectedSchedule({ scheduleId: nextSchedule.scheduleId }));
    }
  };

  const updateFromCacheOrFetch = async () => {
    setIsLoading(true);

    if (venueSmartspotDataIsLoading) {
      return;
    }

    let opportunitiesMap = {};
    if (OPPORTUNITIES_CACHE.has(opportunitiesKey)) {
      opportunitiesMap = OPPORTUNITIES_CACHE.get(opportunitiesKey);
    } else {
      opportunitiesMap = await getOpportunitiesMap(user, activeVenueId);
      OPPORTUNITIES_CACHE.set(opportunitiesKey, opportunitiesMap);
    }

    const cachedVenueSchedulesData = getCachedVenueSchedulesForPresentation(venueIdDateKey, includeListPresentation);

    if (cachedVenueSchedulesData) {
      const derivedData = await getDerivedVenueSchedulesData(cachedVenueSchedulesData, opportunitiesMap);
      setVenueSchedules(derivedData);
      selectNextUpcomingWhenRequested(derivedData.schedules);
    } else {
      const venueSchedulesData = await fetchVenueSchedules();
      const derivedData = await getDerivedVenueSchedulesData(venueSchedulesData, opportunitiesMap);
      setVenueSchedules(derivedData);
      selectNextUpcomingWhenRequested(derivedData.schedules);

      VENUE_SCHEDULES_CACHE.set(venueIdDateKey, { ...venueSchedulesData, includeListPresentation });
    }

    setIsLoading(false);
  };

  updateFromCacheOrFetchRef.current = updateFromCacheOrFetch;

  const loadMoreVenueSchedules = async ({ date, page, venueId }) => {
    setIsLoadingMore(true);

    let opportunitiesMap = {};
    if (OPPORTUNITIES_CACHE.has(opportunitiesKey)) {
      opportunitiesMap = OPPORTUNITIES_CACHE.get(opportunitiesKey);
    } else {
      opportunitiesMap = await getOpportunitiesMap(user, activeVenueId);
      OPPORTUNITIES_CACHE.set(opportunitiesKey, opportunitiesMap);
    }

    const cachedVenueSchedulesData = getCachedVenueSchedulesForPresentation(venueIdDateKey, includeListPresentation);

    if (cachedVenueSchedulesData) {
      const { filterCounts: cachedFilterCounts, schedules: existingSchedules } = cachedVenueSchedulesData;
      const {
        filterCounts: pageFilterCounts,
        pagination,
        schedules,
      } = await fetchMoreVenueSchedules({
        date,
        page,
        venueId,
      });

      if (!_isEmpty(pagination) && schedules.length > 0) {
        const combinedSchedulesData = {
          filterCounts: resolveScheduleFilterCounts(cachedFilterCounts, pageFilterCounts),
          includeListPresentation,
          pagination,
          schedules: [...existingSchedules, ...schedules],
        };
        setVenueSchedules(await getDerivedVenueSchedulesData(combinedSchedulesData, opportunitiesMap));

        VENUE_SCHEDULES_CACHE.set(venueIdDateKey, combinedSchedulesData);
      }
    }

    setIsLoadingMore(false);
  };

  useEffect(() => {
    setActiveSchedule({});
    if (!venueSchedules?.schedules || !activeScheduleId || !activeVenueId) {
      return;
    }

    const schedule = findActiveSchedule(venueSchedules?.schedules, activeScheduleId);
    setActiveSchedule(schedule);

    if (schedule && schedule?.venueId === activeVenueId) {
      dispatch(fetchScheduleData(activeVenueId, schedule?.scheduleId));
    }
  }, [activeScheduleId, activeVenueId, venueSchedules]);

  useEffect(() => {
    setVenueSchedules({});
  }, [scheduleStartDateString, activeVenueId]);

  useEffect(() => {
    if (!activeVenueId || !schedulesStartDate) return;
    if (scheduleListV2Flag === null) {
      setIsLoading(true);
      return;
    }

    updateFromCacheOrFetch();
  }, [scheduleStartDateString, activeVenueId, venueSmartspotDataIsLoading, scheduleListV2Flag]);

  useEffect(() => {
    const onCacheRefresh = () => {
      updateFromCacheOrFetchRef.current();
    };

    CACHE_EVENT_TARGET.addEventListener(CACHE_REFRESH_EVENT, onCacheRefresh);

    return () => {
      CACHE_EVENT_TARGET.removeEventListener(CACHE_REFRESH_EVENT, onCacheRefresh);
    };
  }, []);

  const value = {
    activeSchedule,
    filterCounts: venueSchedules?.dayKey === venueIdDateKey ? venueSchedules.filterCounts : undefined,
    isLoading,
    isLoadingMore,
    loadMoreVenueSchedules,
    pagination: venueSchedules?.pagination || {},
    requestNextUpcomingSelection,
    schedules: venueSchedules?.schedules || [],
  };

  return <VenueSchedulesContext.Provider value={value}>{children}</VenueSchedulesContext.Provider>;
}

VenueSchedulesContextProvider.propTypes = {
  children: PropTypes.node,
};

export default VenueSchedulesContext;
